import {
  computeFlightEnd,
  DEAL_STAGES_V2,
  formatDollars,
  OFFER_BUNDLES,
  PRICING_TIERS,
} from '@/components/deal/mockDealData';
import { videoApiClient } from '@/services/videoApi';

const STAGE_ORDER = Object.fromEntries(DEAL_STAGES_V2.map((stage, index) => [stage.key, index]));

function normalizeBackendMediaUrl(url) {
  if (typeof url !== 'string' || url.length === 0) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${videoApiClient.baseUrl}${url}`;
  }
  return url;
}

function normalizeFrontendPublicUrl(url) {
  if (typeof url !== 'string' || url.length === 0) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('mailto:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${url}`;
    }
    return url;
  }
  return url;
}

function toIsoDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
}

function safeNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function safeNullableNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function compactAddress(parts = []) {
  return parts.filter(Boolean).join(', ') || '';
}

function buildBusinessCategory(business) {
  if (!business?.brand_identity || typeof business.brand_identity !== 'object') return null;
  return business.brand_identity.category
    || business.brand_identity.primary_category
    || business.brand_identity.vertical
    || null;
}

function extractWebsite(originJson = {}) {
  return originJson.website_url || originJson.website || null;
}

function buildPricingFromConfig(config = {}, budgetValue = 0) {
  const selectedPackage = config.selected_package || {};
  if (selectedPackage.pricing) {
    return {
      ...selectedPackage.pricing,
      sellPrice: safeNumber(selectedPackage.pricing.sellPrice, budgetValue),
      platformCost: safeNumber(selectedPackage.pricing.platformCost, budgetValue),
      sellerEarnings: safeNumber(selectedPackage.pricing.sellerEarnings, 0),
      marginPct: safeNumber(selectedPackage.pricing.marginPct, 0),
      markupPct: safeNumber(selectedPackage.pricing.markupPct, 0),
    };
  }

  const pricingSummary = config.pricing_summary || {};
  const sellerEconomics = config.seller_economics || {};
  const sellPrice = safeNumber(
    pricingSummary.sellPrice ?? pricingSummary.sell_price,
    budgetValue,
  );
  const platformCost = safeNumber(
    pricingSummary.platformCost ?? pricingSummary.campaign_cost,
    budgetValue,
  );
  const sellerEarnings = safeNumber(
    pricingSummary.sellerEarnings ?? pricingSummary.seller_earnings ?? sellerEconomics.sellerEarnings,
    sellPrice - platformCost,
  );
  const marginPct = safeNumber(
    pricingSummary.marginPct ?? pricingSummary.margin_pct ?? sellerEconomics.marginPct,
    sellPrice > 0 ? sellerEarnings / sellPrice : 0,
  );
  const markupPct = safeNumber(
    pricingSummary.markupPct ?? pricingSummary.markup_pct ?? sellerEconomics.markupPct,
    platformCost > 0 ? sellerEarnings / platformCost : 0,
  );

  return {
    sellPrice,
    platformCost,
    sellerEarnings,
    marginPct,
    markupPct,
    tierId: pricingSummary.tierId || pricingSummary.tier_id || sellerEconomics.tierId || 'tier-standard',
  };
}

function buildTargetingFromConfig(config = {}, businessName = null) {
  const selectedPackage = config.selected_package || {};
  if (selectedPackage.targetArea || selectedPackage.targetAddress) {
    return {
      type: selectedPackage.targetArea || 'standard_area',
      label: selectedPackage.targetAddress || (businessName ? `Around ${businessName}` : 'Not set'),
      center: selectedPackage.targetCenter || '',
      radius: selectedPackage.radius || null,
      zipCodes: selectedPackage.targetZips || '',
    };
  }

  const targeting = config.targeting || {};
  const label = targeting.label
    || targeting.summary
    || (businessName ? `Around ${businessName}` : 'Not set');
  return {
    type: targeting.mode || targeting.type || 'standard_area',
    label,
    center: targeting.center || '',
    radius: targeting.radius || null,
    zipCodes: Array.isArray(targeting.zip_codes) ? targeting.zip_codes.join(', ') : targeting.zip_codes || '',
  };
}

function buildFlightFromConfig(config = {}) {
  const selectedPackage = config.selected_package || {};
  if (selectedPackage.duration || selectedPackage.flight) {
    const weeks = safeNumber(selectedPackage.duration?.weeks || selectedPackage.flight, 2);
    return {
      weeks,
      label: selectedPackage.duration?.label || `${weeks} weeks`,
      start: selectedPackage.duration?.start || null,
      end: selectedPackage.duration?.end || null,
    };
  }

  const flight = config.flight || {};
  const weeks = safeNumber(flight.weeks, 2);
  return {
    weeks,
    label: flight.label || `${weeks} weeks`,
    start: flight.start_date || null,
    end: flight.end_date || null,
  };
}

function findTemplate(bundleId) {
  return OFFER_BUNDLES.find((bundle) => bundle.id === bundleId) || null;
}

function mapCreativeStateToStatus(state) {
  const normalized = typeof state === 'string' ? state.toLowerCase() : '';

  switch (normalized) {
    case 'completed':
    case 'complete':
    case 'ready':
    case 'succeeded':
    case 'success':
      return 'ready';
    case 'failed':
    case 'failure':
    case 'error':
    case 'errored':
      return 'failed';
    case 'rejected':
    case 'cancelled':
    case 'canceled':
      return 'rejected';
    case 'queued':
    case 'pending':
    case 'processing':
    case 'running':
    case 'rendering':
    case 'generating':
    case 'retrying':
    case 'restarting':
    case 'accepted':
    case 'data_ready':
      return 'processing';
    case 'archived':
      return 'archived';
    case 'draft':
    case 'created':
    case 'new':
      return 'draft';
    default:
      return 'draft';
  }
}

export function deriveCreativeDisplayName({
  creativeName = null,
  objective = null,
} = {}) {
  const explicitName = typeof creativeName === 'string' ? creativeName.trim() : '';
  if (explicitName) return explicitName;

  switch (objective) {
    case 'awareness':
      return 'Brand Awareness';
    case 'seasonal':
      return 'Seasonal Campaign';
    case 'grand_opening':
      return 'Grand Opening';
    default:
      return objective
        ? String(objective)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
        : 'Spec Ad';
  }
}

function buildCreativeAssets(item, thumbnailUrl, previewUrl, shareUrl, displayName = null) {
  const candidateUrl = previewUrl || shareUrl || '';
  if (!candidateUrl) return [];

  return [{
    type: 'video',
    url: candidateUrl,
    thumbnail_url: thumbnailUrl || '',
    label: displayName || `${item.business?.name || 'Creative'} preview`,
  }];
}

export function businessRecordFromWorkspace(workspace, advertiser = null) {
  const business = workspace?.business || {};
  const originJson = workspace?.origin_json || {};
  const advertiserId = workspace?.advertiser_id || advertiser?.id || originJson.advertiserId || originJson.advertiser_id || null;
  const name = business.name || advertiser?.name || originJson.businessName || originJson.name || 'Business';
  const address = business.address || originJson.address || compactAddress([business.city, business.state]);

  return {
    id: advertiserId,
    advertiserId,
    workspaceId: workspace?.id || null,
    organizationId: workspace?.organization_id || advertiser?.organization_id || null,
    name,
    address,
    category: buildBusinessCategory(business) || originJson.category || null,
    rating: business.stars ?? originJson.rating ?? null,
    reviews: business.review_count ?? originJson.reviews ?? null,
    website_url: extractWebsite(originJson),
    hero_image_url: business.hero_image_url || originJson.hero_image_url || null,
    scope: originJson.scope || null,
    createdAt: workspace?.created_at || advertiser?.created_at || null,
    updatedAt: workspace?.updated_at || advertiser?.updated_at || null,
    origin: originJson,
  };
}

export function businessProfileFromBusinessRecord(businessRecord = {}) {
  return {
    id: businessRecord.id || null,
    place_id: businessRecord.origin?.place_id || businessRecord.origin?.placeId || null,
    website_url: businessRecord.website_url || null,
    name: businessRecord.name || null,
    address: businessRecord.address || null,
    location_geo: businessRecord.origin?.location_geo || businessRecord.origin?.location || null,
    category: businessRecord.category || null,
    photos: businessRecord.origin?.photos || 0,
    logo_url: businessRecord.origin?.logo_url || null,
    reviews: businessRecord.reviews || 0,
    rating: businessRecord.rating || 0,
  };
}

export function businessProfileFromWorkspaceShell(workspace = {}, businessRecord = null) {
  const shellProfile = workspace?.business_profile;
  if (!shellProfile?.advertiser_id) {
    return businessProfileFromBusinessRecord(businessRecord || {});
  }

  const base = businessProfileFromBusinessRecord(businessRecord || {});

  return {
    ...base,
    id: shellProfile.advertiser_id,
    advertiserId: shellProfile.advertiser_id,
    workspaceId: workspace?.id || businessRecord?.workspaceId || null,
    enrichmentStatus: shellProfile.enrichment_status || 'pending',
    enrichmentStartedAt: toIsoDate(shellProfile.enrichment_started_at),
    enrichmentCompletedAt: toIsoDate(shellProfile.enrichment_completed_at),
    enrichmentFailedAt: toIsoDate(shellProfile.enrichment_failed_at),
    enrichmentErrorDetail: shellProfile.enrichment_error_detail || null,
    business: workspace?.business || null,
    sourceRefsJson: workspace?.origin_json || businessRecord?.origin || null,
    enrichmentSourceJson: workspace?.origin_json || businessRecord?.origin || null,
    canonicalProfileJson: null,
    createdAt: toIsoDate(workspace?.created_at || businessRecord?.createdAt),
    updatedAt: toIsoDate(workspace?.updated_at || businessRecord?.updatedAt),
  };
}

export function businessProfileFromBackend(profile = {}, options = {}) {
  const business = profile.business || options.business || {};
  const sourceRefs = profile.source_refs_json || options.sourceRefsJson || {};
  const enrichmentSource = profile.enrichment_source_json || options.enrichmentSourceJson || sourceRefs || {};
  const canonicalProfile = profile.canonical_profile_json || {};
  const brandIdentity = business.brand_identity || canonicalProfile.brand_identity || {};
  const address = business.address
    || canonicalProfile.address
    || sourceRefs.address
    || options.address
    || compactAddress([business.city, business.state]);
  const websiteUrl = business.website_url
    || canonicalProfile.website_url
    || sourceRefs.website_url
    || options.website_url
    || null;
  const name = business.name
    || canonicalProfile.name
    || sourceRefs.name
    || options.name
    || null;
  const category = buildBusinessCategory(business)
    || canonicalProfile.category
    || enrichmentSource.category
    || sourceRefs.category
    || options.category
    || null;

  return {
    id: profile.advertiser_id || options.id || null,
    advertiserId: profile.advertiser_id || options.id || null,
    organizationId: profile.organization_id || options.organizationId || null,
    workspaceId: profile.workspace_id || options.workspaceId || null,
    enrichmentStatus: profile.enrichment_status || 'pending',
    enrichmentStartedAt: toIsoDate(profile.enrichment_started_at),
    enrichmentCompletedAt: toIsoDate(profile.enrichment_completed_at),
    enrichmentFailedAt: toIsoDate(profile.enrichment_failed_at),
    enrichmentErrorDetail: profile.enrichment_error_detail || null,
    sourceRefsJson: sourceRefs || null,
    enrichmentSourceJson: enrichmentSource || null,
    canonicalProfileJson: canonicalProfile || null,
    business: Object.keys(business).length ? business : null,
    place_id: sourceRefs.place_id || enrichmentSource.place_id || options.place_id || null,
    maps_url: sourceRefs.maps_url || enrichmentSource.maps_url || options.maps_url || null,
    website_url: websiteUrl,
    name,
    address,
    location_geo: sourceRefs.location_geo || enrichmentSource.location_geo || options.location_geo || null,
    category,
    photos: business.photos_count ?? canonicalProfile.photos_count ?? sourceRefs.photos ?? options.photos ?? 0,
    logo_url: business.logo_url ?? canonicalProfile.logo_url ?? options.logo_url ?? null,
    reviews: business.review_count ?? canonicalProfile.review_count ?? sourceRefs.reviews ?? options.reviews ?? 0,
    rating: business.stars ?? canonicalProfile.stars ?? sourceRefs.rating ?? options.rating ?? 0,
    phone: business.phone || canonicalProfile.phone || options.phone || null,
    createdAt: toIsoDate(profile.created_at || options.createdAt),
    updatedAt: toIsoDate(profile.updated_at || options.updatedAt),
    brandIdentity,
  };
}

export function creativeFromBackend(item, selectedBindingId = null) {
  const thumbnailUrl = normalizeBackendMediaUrl(
    item.thumbnail_url || (item.job_id ? videoApiClient.getThumbnailUrl(item.job_id) : ''),
  );
  const previewUrl = normalizeBackendMediaUrl(
    item.preview_url || item.video_url || (item.state === 'completed' && item.job_id ? videoApiClient.getAssetUrl(item.job_id) : ''),
  );
  const shareUrl = item.share_url || previewUrl || '';
  const aspectRatio = item.aspect_ratio || item.business?.brand_identity?.aspect_ratio || '16:9';
  const format = item.format || (aspectRatio === '9:16' ? 'social_9_16' : 'ctv_16_9');
  const objective = item.objective || item.business?.brand_identity?.objective || 'awareness';
  const creativeName = item.creative_name || null;
  const displayName = deriveCreativeDisplayName({ creativeName, objective });

  return {
    id: item.binding_id,
    bindingId: item.binding_id,
    creativeSetId: item.job_id,
    jobId: item.job_id,
    businessId: item.advertiser_id,
    creativeName,
    displayName,
    status: mapCreativeStateToStatus(item.state),
    rawState: item.state,
    objective,
    tone: item.tone || 'auto',
    scope: item.scope || null,
    format,
    aspectRatio,
    duration: item.duration || item.business?.brand_identity?.duration || 15,
    thumbnailUrl,
    previewUrl,
    videoUrl: normalizeBackendMediaUrl(item.video_url || previewUrl || ''),
    shareUrl: normalizeBackendMediaUrl(shareUrl),
    statusUrl: normalizeBackendMediaUrl(item.status_url || ''),
    projectFolder: item.project_folder || null,
    errorDetail: item.error_detail || null,
    generationRequestJson: item.generation_request_json || null,
    business: item.business || null,
    assets: buildCreativeAssets(item, thumbnailUrl, previewUrl, shareUrl, displayName),
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    proposalIds: [],
    isSelected: Boolean(item.selected_on_workspace) || item.binding_id === selectedBindingId,
    selectedOnWorkspace: Boolean(item.selected_on_workspace) || item.binding_id === selectedBindingId,
  };
}

export function selectedWorkspaceCreativeFromBackend(selectedCreative, options = {}) {
  if (!selectedCreative?.binding_id) return null;

  const jobId = selectedCreative.job_id;
  const thumbnailUrl = normalizeBackendMediaUrl(
    selectedCreative.thumbnail_url || (jobId ? videoApiClient.getThumbnailUrl(jobId) : ''),
  );
  const previewUrl = normalizeBackendMediaUrl(
    selectedCreative.preview_url
    || selectedCreative.video_url
    || (jobId && selectedCreative.state === 'completed' ? videoApiClient.getAssetUrl(jobId) : ''),
  );
  const shareUrl = selectedCreative.share_url || previewUrl || '';
  const aspectRatio = selectedCreative.aspect_ratio || options.aspectRatio || '16:9';
  const format = selectedCreative.format || (aspectRatio === '9:16' ? 'social_9_16' : 'ctv_16_9');
  const objective = selectedCreative.objective || options.objective || 'awareness';
  const creativeName = selectedCreative.creative_name || null;
  const displayName = deriveCreativeDisplayName({ creativeName, objective });

  return {
    id: selectedCreative.binding_id,
    bindingId: selectedCreative.binding_id,
    creativeSetId: jobId,
    jobId,
    businessId: options.businessId || null,
    creativeName,
    displayName,
    status: mapCreativeStateToStatus(selectedCreative.state),
    rawState: selectedCreative.state,
    objective,
    tone: selectedCreative.tone || options.tone || 'auto',
    scope: selectedCreative.scope || options.scope || null,
    format,
    aspectRatio,
    duration: selectedCreative.duration || options.duration || 15,
    thumbnailUrl,
    previewUrl,
    videoUrl: normalizeBackendMediaUrl(selectedCreative.video_url || previewUrl || ''),
    shareUrl: normalizeBackendMediaUrl(shareUrl),
    statusUrl: normalizeBackendMediaUrl(selectedCreative.status_url || ''),
    projectFolder: selectedCreative.project_folder || null,
    errorDetail: selectedCreative.error_detail || null,
    generationRequestJson: selectedCreative.generation_request_json || null,
    business: options.business || null,
    assets: buildCreativeAssets(
      { business: { name: options.businessName || 'Selected creative' } },
      thumbnailUrl,
      previewUrl,
      shareUrl,
      displayName,
    ),
    createdAt: selectedCreative.created_at,
    updatedAt: selectedCreative.updated_at,
    proposalIds: [],
    isSelected: true,
    selectedOnWorkspace: true,
    isWorkspaceFallback: true,
  };
}

export function pitchBootstrapFromBackend(response = {}) {
  const creative = creativeFromBackend(response, response.selected_on_workspace ? response.binding_id : null);
  const rawLength = response.video_config?.length;
  const length = Number.isFinite(Number(rawLength)) ? Number(rawLength) : null;
  const business = response.business && typeof response.business === 'object'
    ? response.business
    : null;
  const videoConfig = response.video_config && typeof response.video_config === 'object'
    ? {
      aspectRatio: response.video_config.aspect_ratio || null,
      resolution: response.video_config.resolution || null,
      length,
      videoProvider: response.video_config.video_provider || null,
      scriptMode: response.video_config.script_mode || null,
      logoOverlayEnabled: Boolean(response.video_config.logo_overlay_enabled),
      logoOverlayPosition: response.video_config.logo_overlay_position || null,
      logoOverlayOpacity: typeof response.video_config.logo_overlay_opacity === 'number'
        ? response.video_config.logo_overlay_opacity
        : null,
    }
    : null;
  const director = response.director && typeof response.director === 'object'
    ? {
      businessDataUrl: normalizeBackendMediaUrl(response.director.business_data_url || ''),
      scriptUrl: normalizeBackendMediaUrl(response.director.script_url || ''),
      videoConfigUrl: normalizeBackendMediaUrl(response.director.video_config_url || ''),
      campaignBriefUrl: normalizeBackendMediaUrl(response.director.campaign_brief_url || ''),
      charactersUrl: normalizeBackendMediaUrl(response.director.characters_url || ''),
    }
    : null;

  return {
    workspaceId: response.workspace_id || null,
    advertiserId: response.advertiser_id || null,
    bindingId: response.binding_id || null,
    jobId: response.job_id || null,
    businessSnapshotId: response.business_snapshot_id || null,
    creativeName: response.creative_name || creative.creativeName || null,
    reusedExisting: Boolean(response.reused_existing),
    state: response.state || null,
    selectedOnWorkspace: Boolean(response.selected_on_workspace),
    creative,
    business,
    videoConfig,
    director,
    createdAt: toIsoDate(response.created_at),
    updatedAt: toIsoDate(response.updated_at),
  };
}

export function creativeSetFromCreative(creative, businessName = 'Business') {
  if (!creative) return null;

  const previewUrl = creative.previewUrl || creative.videoUrl || creative.shareUrl || '';
  const thumbnailUrl = creative.thumbnailUrl || '';
  const displayName = deriveCreativeDisplayName({
    creativeName: creative.creativeName || creative.creative_name,
    objective: creative.objective,
  });

  return {
    id: creative.bindingId || creative.id,
    binding_id: creative.bindingId || creative.id,
    job_id: creative.jobId || creative.creativeSetId || null,
    creative_name: creative.creativeName || creative.creative_name || null,
    display_name: displayName,
    status: creative.status,
    raw_state: creative.rawState || null,
    objective: creative.objective || 'awareness',
    tone: creative.tone || 'auto',
    format: creative.format || 'ctv_16_9',
    aspect_ratio: creative.aspectRatio || '16:9',
    duration: creative.duration || 15,
    error_detail: creative.errorDetail || null,
    share_url: creative.shareUrl || previewUrl || '',
    preview_url: previewUrl || '',
    video_url: creative.videoUrl || previewUrl || '',
    thumbnail_url: thumbnailUrl,
    assets: previewUrl ? [{
      type: 'video',
      url: previewUrl,
      thumbnail_url: thumbnailUrl,
      label: displayName,
    }] : [],
  };
}

export function selectedPackageFromOfferConfig(config = {}) {
  if (!config || Object.keys(config).length === 0) return null;

  const selectedPackage = config.selected_package || {};
  if (selectedPackage.id || selectedPackage.bundleId || selectedPackage.name) {
    return selectedPackage;
  }

  const templateKey = config.template_key || config.preset_key || null;
  const template = findTemplate(templateKey);
  const budgetValue = safeNumber(config.budget?.amount ?? config.budget?.value, template?.investmentRange?.default || 0);
  const pricing = buildPricingFromConfig(config, budgetValue);
  const flight = buildFlightFromConfig(config);
  const targeting = buildTargetingFromConfig(config);

  return {
    id: templateKey || 'offer-config',
    bundleId: templateKey || null,
    name: template?.name || config.package_name || 'Campaign offer',
    budget: budgetValue,
    cpmCap: safeNumber(config.ad_view_cpm, 0),
    inventoryTier: config.inventory_tier || null,
    flight: flight.weeks,
    duration: flight,
    targetArea: targeting.type,
    targetAddress: targeting.label,
    targetCenter: targeting.center,
    targetZips: targeting.zipCodes,
    radius: targeting.radius,
    pricing,
  };
}

export function offerConfigFromSelectedPackage(selectedPackage = {}) {
  const pricing = selectedPackage.pricing || {};
  const budget = safeNumber(selectedPackage.budget, 0);
  const weeks = safeNumber(selectedPackage.duration?.weeks || selectedPackage.flight, 2);
  const startDate = selectedPackage.duration?.start || new Date().toISOString().split('T')[0];
  const endDate = selectedPackage.duration?.end || computeFlightEnd(startDate, weeks);
  const zipCodes = selectedPackage.targetZips
    ? String(selectedPackage.targetZips).split(',').map((value) => value.trim()).filter(Boolean)
    : [];

  return {
    template_key: selectedPackage.bundleId || selectedPackage.id || null,
    preset_key: selectedPackage.bundleId || selectedPackage.id || null,
    package_name: selectedPackage.name || null,
    ad_view_cpm: safeNumber(selectedPackage.cpmCap, 0),
    budget: {
      amount: budget,
      currency: 'USD',
    },
    flight: {
      weeks,
      label: selectedPackage.duration?.label || `${weeks} weeks`,
      start_date: startDate,
      end_date: endDate,
    },
    targeting: {
      mode: selectedPackage.targetArea || 'standard_area',
      label: selectedPackage.targetAddress || '',
      center: selectedPackage.targetCenter || '',
      radius: selectedPackage.radius || null,
      zip_codes: zipCodes,
    },
    pricing_summary: {
      sellPrice: safeNumber(pricing.sellPrice, budget),
      sell_price: safeNumber(pricing.sellPrice, budget),
      platformCost: safeNumber(pricing.platformCost, budget),
      campaign_cost: safeNumber(pricing.platformCost, budget),
      sellerEarnings: safeNumber(pricing.sellerEarnings, 0),
      seller_earnings: safeNumber(pricing.sellerEarnings, 0),
      marginPct: safeNumber(pricing.marginPct, 0),
      margin_pct: safeNumber(pricing.marginPct, 0),
      markupPct: safeNumber(pricing.markupPct, 0),
      markup_pct: safeNumber(pricing.markupPct, 0),
      tierId: pricing.tierId || 'tier-standard',
      tier_id: pricing.tierId || 'tier-standard',
    },
    seller_economics: {
      sellerEarnings: safeNumber(pricing.sellerEarnings, 0),
      seller_earnings: safeNumber(pricing.sellerEarnings, 0),
      marginPct: safeNumber(pricing.marginPct, 0),
      margin_pct: safeNumber(pricing.marginPct, 0),
      markupPct: safeNumber(pricing.markupPct, 0),
      markup_pct: safeNumber(pricing.markupPct, 0),
      tierId: pricing.tierId || 'tier-standard',
      tier_id: pricing.tierId || 'tier-standard',
    },
    selected_package: selectedPackage,
  };
}

export function proposalFromBackend(proposal) {
  const snapshot = proposal.offer_snapshot_json || {};
  const selectedPackage = snapshot.selected_package || selectedPackageFromOfferConfig(snapshot) || {};
  const budgetCap = safeNumber(
    selectedPackage.budget ?? snapshot.budget?.amount ?? snapshot.pricing_summary?.platformCost,
    0,
  );
  const pricing = buildPricingFromConfig(snapshot, budgetCap);
  const flight = buildFlightFromConfig(snapshot);
  const targeting = buildTargetingFromConfig(snapshot);
  const deliveryEstimate = proposalDeliveryEstimateFromBackend(proposal.delivery_estimate);
  const activeShareLink = proposal.active_share_link
    ? {
      id: proposal.active_share_link.id,
      proposalId: proposal.active_share_link.proposal_id,
      proposalVersion: proposal.active_share_link.proposal_version,
      status: proposal.active_share_link.status,
      expiresAt: toIsoDate(proposal.active_share_link.expires_at),
      createdAt: toIsoDate(proposal.active_share_link.created_at),
      updatedAt: toIsoDate(proposal.active_share_link.updated_at),
      revokedAt: toIsoDate(proposal.active_share_link.revoked_at),
      lastViewedAt: toIsoDate(proposal.active_share_link.last_viewed_at),
      viewCount: safeNumber(proposal.active_share_link.view_count, 0),
      recipientEmail: proposal.active_share_link.recipient_email || null,
      deliveryChannel: proposal.active_share_link.delivery_channel || null,
      publicUrl: normalizeFrontendPublicUrl(proposal.active_share_link.public_url || ''),
    }
    : null;
  const publicUrl = normalizeFrontendPublicUrl(
    proposal.public_url || activeShareLink?.publicUrl || '',
  );

  return {
    id: proposal.id,
    workspaceId: proposal.workspace_id,
    advertiserId: proposal.advertiser_id,
    version: proposal.version,
    previousVersionId: proposal.previous_version_id,
    status: proposal.status,
    selected_creative_binding_id: proposal.selected_creative_binding_id,
    selected_job_id: proposal.selected_job_id,
    creativeId: proposal.selected_creative_binding_id,
    creative_set_id: proposal.selected_job_id,
    offer_snapshot_json: snapshot,
    packageName: proposal.package_name || selectedPackage.name || 'Campaign proposal',
    package_id: selectedPackage.id || selectedPackage.bundleId || snapshot.template_key || null,
    budget_cap: budgetCap,
    price: safeNumber(pricing.sellPrice, budgetCap),
    pricing,
    flight,
    target_area: targeting,
    deliveryEstimate,
    pitch_message: proposal.pitch_message || '',
    deliveryMethod: proposal.delivery_method || 'link',
    recipientEmail: proposal.recipient_email || proposal.contact_json?.email || proposal.contact_json?.recipient_email || '',
    contact: proposal.contact_json || {},
    followUpDays: proposal.follow_up_days,
    activeShareLink,
    publicUrl,
    emailSubject: proposal.email_subject || null,
    emailBodyText: proposal.email_body_text || null,
    created_at: proposal.created_at,
    updated_at: proposal.updated_at,
    sent_at: proposal.sent_at,
    accepted_at: proposal.accepted_at,
    changes_requested_at: proposal.changes_requested_at,
    expired_at: proposal.expired_at,
    lost_at: proposal.lost_at,
    commercial_locked_at: proposal.commercial_locked_at,
  };
}

export function proposalDeliveryEstimateFromBackend(deliveryEstimate = null) {
  if (!deliveryEstimate || typeof deliveryEstimate !== 'object') {
    return null;
  }

  return {
    label: deliveryEstimate.label || null,
    estimateBasis: deliveryEstimate.estimate_basis || null,
    estimatedImpressionsLow: safeNullableNumber(deliveryEstimate.estimated_impressions_low),
    estimatedImpressionsHigh: safeNullableNumber(deliveryEstimate.estimated_impressions_high),
    estimatedReachLow: safeNullableNumber(deliveryEstimate.estimated_reach_low),
    estimatedReachHigh: safeNullableNumber(deliveryEstimate.estimated_reach_high),
    assumptions: deliveryEstimate.assumptions || null,
    disclaimerText: deliveryEstimate.disclaimer_text || null,
  };
}

export function publicProposalFromBackend(proposal = {}) {
  const selectedCreative = proposal.selected_creative
    ? {
      creativeName: proposal.selected_creative.creative_name || null,
      displayName: deriveCreativeDisplayName({
        creativeName: proposal.selected_creative.creative_name,
        objective: proposal.selected_creative.objective,
      }),
      previewUrl: normalizeBackendMediaUrl(
        proposal.selected_creative.preview_url
        || proposal.selected_creative.share_url
        || '',
      ),
      thumbnailUrl: normalizeBackendMediaUrl(proposal.selected_creative.thumbnail_url || ''),
      shareUrl: normalizeBackendMediaUrl(proposal.selected_creative.share_url || ''),
      objective: proposal.selected_creative.objective || null,
      tone: proposal.selected_creative.tone || null,
      scope: proposal.selected_creative.scope || null,
      format: proposal.selected_creative.format || null,
      aspectRatio: proposal.selected_creative.aspect_ratio || null,
      duration: proposal.selected_creative.duration || null,
    }
    : null;

  return {
    state: 'ready',
    proposalId: proposal.proposal_id || null,
    proposalVersion: proposal.proposal_version || null,
    status: proposal.status || null,
    advertiserName: proposal.advertiser_name || null,
    business: proposal.business || null,
    packageName: proposal.package_name || 'Campaign proposal',
    pitchMessage: proposal.pitch_message || '',
    pricingSummary: proposal.pricing_summary_json || null,
    targetingSummary: proposal.targeting_summary_json || null,
    flightSummary: proposal.flight_summary_json || null,
    deliveryEstimate: proposalDeliveryEstimateFromBackend(proposal.delivery_estimate),
    selectedCreative,
    sellerContact: proposal.seller_contact
      ? {
        name: proposal.seller_contact.name || null,
        email: proposal.seller_contact.email || null,
        phone: proposal.seller_contact.phone || null,
        title: proposal.seller_contact.title || null,
        company: proposal.seller_contact.company || null,
      }
      : null,
    shareLink: proposal.share_link
      ? {
        status: proposal.share_link.status || null,
        expiresAt: toIsoDate(proposal.share_link.expires_at),
        revokedAt: toIsoDate(proposal.share_link.revoked_at),
        lastViewedAt: toIsoDate(proposal.share_link.last_viewed_at),
        viewCount: safeNumber(proposal.share_link.view_count, 0),
        publicUrl: normalizeFrontendPublicUrl(proposal.share_link.public_url || ''),
      }
      : null,
  };
}

export function publicProposalErrorStateFromApi(error) {
  const state = error?.publicProposalState || 'invalid';

  switch (state) {
    case 'expired':
      return {
        state,
        title: 'Proposal link expired',
        description: 'This proposal link is no longer active. Ask the seller for a fresh link.',
      };
    case 'revoked':
      return {
        state,
        title: 'Proposal link revoked',
        description: 'This proposal link has been turned off. Contact the seller for the latest proposal link.',
      };
    case 'invalid':
    default:
      return {
        state: 'invalid',
        title: 'Proposal link invalid',
        description: 'This proposal link could not be found. Check the URL or ask the seller to resend it.',
      };
  }
}

export function proposalPatchFromUi(proposal, selectedPackage = null) {
  const sourceProposal = proposal || {};
  const pricing = sourceProposal.pricing || selectedPackage?.pricing || {};
  const weeks = safeNumber(sourceProposal.flight?.weeks || selectedPackage?.duration?.weeks || selectedPackage?.flight, 2);
  const startDate = sourceProposal.flight?.start || selectedPackage?.duration?.start || new Date().toISOString().split('T')[0];
  const endDate = sourceProposal.flight?.end || selectedPackage?.duration?.end || computeFlightEnd(startDate, weeks);

  return {
    selected_creative_binding_id: sourceProposal.creativeId || null,
    offer_snapshot_json: {
      ...offerConfigFromSelectedPackage({
        ...(selectedPackage || {}),
        pricing: pricing,
        duration: {
          ...(selectedPackage?.duration || {}),
          weeks,
          label: sourceProposal.flight?.label || selectedPackage?.duration?.label || `${weeks} weeks`,
          start: startDate,
          end: endDate,
        },
        targetArea: sourceProposal.target_area?.type || selectedPackage?.targetArea,
        targetAddress: sourceProposal.target_area?.label || selectedPackage?.targetAddress,
        targetCenter: selectedPackage?.targetCenter || '',
        targetZips: selectedPackage?.targetZips || '',
        radius: selectedPackage?.radius || null,
        cpmCap: selectedPackage?.cpmCap || sourceProposal.offer_snapshot_json?.ad_view_cpm || 0,
        budget: selectedPackage?.budget || sourceProposal.budget_cap || 0,
        name: sourceProposal.packageName || selectedPackage?.name,
        id: sourceProposal.package_id || selectedPackage?.id,
        bundleId: selectedPackage?.bundleId || sourceProposal.package_id,
      }),
      package_name: sourceProposal.packageName || selectedPackage?.name || null,
      pitch_message: sourceProposal.pitch_message || '',
      delivery_method: sourceProposal.deliveryMethod || 'link',
      contact_json: sourceProposal.contact || {},
      follow_up_days: sourceProposal.followUpDays ?? null,
      selected_package: {
        ...(selectedPackage || {}),
        id: sourceProposal.package_id || selectedPackage?.id || selectedPackage?.bundleId || null,
        bundleId: selectedPackage?.bundleId || selectedPackage?.id || sourceProposal.package_id || null,
        name: sourceProposal.packageName || selectedPackage?.name || 'Campaign offer',
        budget: selectedPackage?.budget || sourceProposal.budget_cap || 0,
        pricing,
        duration: {
          ...(selectedPackage?.duration || {}),
          weeks,
          label: sourceProposal.flight?.label || selectedPackage?.duration?.label || `${weeks} weeks`,
          start: startDate,
          end: endDate,
        },
        flight: weeks,
        targetArea: sourceProposal.target_area?.type || selectedPackage?.targetArea || 'standard_area',
        targetAddress: sourceProposal.target_area?.label || selectedPackage?.targetAddress || '',
      },
    },
    package_name: sourceProposal.packageName || null,
    pitch_message: sourceProposal.pitch_message || null,
    delivery_method: sourceProposal.deliveryMethod || null,
    contact_json: sourceProposal.contact || null,
    follow_up_days: sourceProposal.followUpDays ?? null,
  };
}

export function agreementFromBackend(agreement) {
  const billingStatus = agreement.billing_status;
  return {
    id: agreement.id,
    workspaceId: agreement.workspace_id,
    advertiserId: agreement.advertiser_id,
    proposalId: agreement.proposal_id,
    proposalVersion: agreement.proposal_version,
    status: agreement.status,
    billing_provider: agreement.billing_provider,
    billing_status: billingStatus,
    billing_mode: agreement.billing_mode,
    payment_link_url: agreement.payment_link_url,
    external_reference: agreement.external_reference,
    requested_at: agreement.requested_at,
    confirmed_at: agreement.confirmed_at,
    metadata_json: agreement.metadata_json || {},
    accepted_at: agreement.accepted_at,
    created_at: agreement.created_at,
    updated_at: agreement.updated_at,
    payment_method_status:
      billingStatus === 'payment_confirmed'
        ? 'confirmed'
        : billingStatus === 'link_sent'
          ? 'link_sent'
          : billingStatus === 'payment_pending'
            ? 'pending'
            : billingStatus === 'payment_failed'
              ? 'failed'
              : billingStatus === 'cancelled'
                ? 'cancelled'
                : 'not_started',
    billing_link_sent_at: agreement.requested_at,
    payment_confirmed_at: agreement.confirmed_at,
  };
}

export function campaignFromBackend(campaign, latestSnapshot = null, reportSummary = null) {
  const metrics = latestSnapshot?.metrics_json || {};
  const config = campaign?.campaign_config_snapshot_json || {};
  const startDate = metrics.start_date || config.flight?.start_date || null;
  const endDate = metrics.end_date || config.flight?.end_date || null;
  const impressions = safeNumber(metrics.impressions ?? reportSummary?.headline_metrics?.impressions, 0);
  const spend = safeNumber(metrics.spend ?? reportSummary?.headline_metrics?.spend, 0);
  const budgetCap = safeNumber(
    metrics.budget_cap ?? config.budget?.amount ?? config.pricing_summary?.platformCost,
    0,
  );
  const daysRemaining = safeNumber(metrics.days_remaining, endDate ? Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0);
  const householdsReached = safeNumber(
    metrics.households_reached ?? metrics.households ?? metrics.unique_households,
    0,
  );
  const cpm = safeNumber(metrics.cpm, impressions > 0 ? (spend / impressions) * 1000 : 0);

  return {
    id: campaign.id,
    status: campaign.status,
    provider: campaign.provider,
    external_reference: campaign.external_reference,
    proposalId: campaign.proposal_id,
    proposalVersion: campaign.proposal_version,
    agreementId: campaign.agreement_id,
    selectedCreativeId: campaign.selected_creative_binding_id,
    selectedJobId: campaign.selected_job_id,
    launched_at: campaign.launched_at,
    created_at: campaign.created_at,
    updated_at: campaign.updated_at,
    start_date: startDate,
    end_date: endDate,
    spend,
    budget_cap: budgetCap,
    households_reached: householdsReached,
    impressions,
    days_remaining: daysRemaining,
    cpm,
    name: config.package_name || 'Live campaign',
    reportSummary,
    latestSnapshot,
  };
}

export function activityFromBackend(event) {
  return {
    id: event.id,
    type: event.type,
    label: event.label,
    detail: event.detail,
    timestamp: event.occurred_at,
    actorType: event.actor_type,
    actorId: event.actor_id,
    metadata: event.metadata_json || {},
  };
}

export function queueLeadFromBackend(item) {
  const business = item.business || {};
  const stage = item.stage;
  const recommended = item.recommended_next_step_text || item.status_label || 'Open workspace';
  const category = buildBusinessCategory(business) || business.brand_identity?.vertical || '';
  const address = business.address || compactAddress([business.city, business.state]);
  const blockers = item.blockers || [];

  return {
    id: item.workspace_id,
    fromBackend: true,
    workspaceId: item.workspace_id,
    advertiserId: item.advertiser_id,
    businessProfileId: item.advertiser_id,
    status: stage === 'completed_or_terminal' ? 'closed' : ['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch', 'live'].includes(stage) ? 'active' : 'qualified',
    businessName: business.name || item.advertiser_name,
    businessCategory: category || item.status_label,
    businessAddress: address,
    businessPhone: '',
    businessWebsite: '',
    businessRating: business.stars ?? null,
    businessReviewsCount: business.review_count ?? null,
    businessPhotos: null,
    triggerEvent: blockers[0]?.message || item.recommended_next_step_text || item.status_label,
    triggerDate: item.last_activity_at || item.updated_at,
    enrichmentStatus: 'enriched',
    advertiserName: item.advertiser_name,
    queueStage: stage,
    queueStatusCode: item.status_code,
    queueStatusLabel: item.status_label,
    recommendedNextStepCode: item.recommended_next_step_code,
    recommendedNextStepText: item.recommended_next_step_text,
    blockers,
    needsAttention: item.needs_attention,
    stale: item.stale,
    staleReason: item.stale_reason,
    currentProposal: item.current_proposal,
    currentAgreement: item.current_agreement,
    currentCampaign: item.current_campaign,
    latestReportingSummary: item.latest_reporting_summary,
    lastActivityAt: item.last_activity_at,
    updatedAt: item.updated_at,
    whyNow: blockers[0]?.message || item.status_label,
    nextActionLabel: recommended,
    nextActionDescription: item.recommended_next_step_text || 'Open this workspace to continue.',
  };
}

export function launchReadinessFromBackend(readiness) {
  const blockers = (readiness.blockers || []).map((blocker) => {
    const mapping = {
      proposal_missing: { key: 'proposal', label: 'Proposal is missing', resolvePath: '/offer', resolveLabel: 'Build offer' },
      proposal_not_accepted: { key: 'proposal', label: 'Proposal is not accepted', resolvePath: '/proposal', resolveLabel: 'Open proposal' },
      billing_missing: { key: 'billing', label: 'Billing is not started', resolvePath: '/close', resolveLabel: 'Open billing' },
      billing_unconfirmed: { key: 'billing', label: 'Billing is not confirmed', resolvePath: '/close', resolveLabel: 'Confirm billing' },
      targeting_missing: { key: 'targeting', label: 'Targeting is missing', resolvePath: '/offer', resolveLabel: 'Edit offer' },
      flight_missing: { key: 'flight', label: 'Flight is missing', resolvePath: '/offer', resolveLabel: 'Edit offer' },
      creative_missing: { key: 'creative', label: 'Creative is missing', resolvePath: '/pitch', resolveLabel: 'Generate creative' },
      creative_not_ready: { key: 'creative', label: 'Creative is not ready', resolvePath: '/pitch', resolveLabel: 'Open creative' },
      selected_creative_missing: { key: 'creative', label: 'No creative is selected', resolvePath: '/pitch', resolveLabel: 'Select creative' },
      contact_missing: { key: 'contact', label: 'Buyer contact is missing', resolvePath: '/proposal', resolveLabel: 'Update proposal' },
    };
    const fallback = { key: blocker.code, label: blocker.message, resolvePath: '/proposal', resolveLabel: 'Review workspace' };
    return {
      ...fallback,
      ...(mapping[blocker.code] || {}),
      detail: blocker.message,
    };
  });

  const readyItems = [];
  if (readiness.current_proposal) {
    readyItems.push({
      key: 'proposal',
      label: 'Proposal is ready',
      detail: `Version ${readiness.current_proposal.version} is ${readiness.current_proposal.status.replace(/_/g, ' ')}.`,
    });
  }
  if (readiness.current_agreement?.billing_status === 'payment_confirmed') {
    readyItems.push({
      key: 'billing',
      label: 'Billing is confirmed',
      detail: 'Payment method is on file.',
    });
  }
  if (readiness.launch_selected_creative_binding_id) {
    readyItems.push({
      key: 'creative',
      label: 'Creative is selected',
      detail: 'A launchable creative binding is attached to this workspace.',
    });
  }

  return {
    status: readiness.status,
    allReady: readiness.ready,
    blockers,
    readyItems,
    items: [...readyItems, ...blockers],
    progressPct: Math.round((readyItems.length / Math.max(readyItems.length + blockers.length, 1)) * 100),
    recommendedNextStepCode: readiness.recommended_next_step_code,
    recommendedNextStepText: readiness.recommended_next_step_text,
  };
}

export function sellerHomeSummaryFromBackend(summary) {
  return {
    totalActiveWorkspaces: summary.total_active_workspaces,
    needsAttentionCount: summary.needs_attention_count,
    readyToLaunchCount: summary.ready_to_launch_count,
    liveCampaignsCount: summary.live_campaigns_count,
    awaitingBuyerCount: summary.awaiting_buyer_count,
    awaitingBillingCount: summary.awaiting_billing_count,
    reportingPendingCount: summary.reporting_pending_count,
    stageBuckets: summary.stage_buckets || [],
  };
}

export function stageBucketsFromSellerHomeSummary(summary = null) {
  if (!summary?.stageBuckets) return [];
  return summary.stageBuckets.map((bucket) => ({
    stage: bucket.stage,
    count: bucket.count,
  }));
}

export function reportMetricsFromSnapshot(snapshot = null, reportSummary = null, campaign = null) {
  const metrics = snapshot?.metrics_json || {};
  return {
    households_reached: safeNumber(metrics.households_reached ?? metrics.households ?? metrics.unique_households, campaign?.households_reached || 0),
    impressions: safeNumber(metrics.impressions ?? reportSummary?.headline_metrics?.impressions, campaign?.impressions || 0),
    spend: safeNumber(metrics.spend ?? reportSummary?.headline_metrics?.spend, campaign?.spend || 0),
    budget_cap: safeNumber(metrics.budget_cap, campaign?.budget_cap || 0),
    days_remaining: safeNumber(metrics.days_remaining, campaign?.days_remaining || 0),
    cpm: safeNumber(metrics.cpm, campaign?.cpm || 0),
    start_date: metrics.start_date || campaign?.start_date || null,
    end_date: metrics.end_date || campaign?.end_date || null,
  };
}

export function proposalActionsFromStatus(status) {
  return {
    isEditable: ['draft', 'changes_requested'].includes(status),
    isAwaitingBuyer: status === 'sent',
    isTerminal: ['accepted', 'expired', 'lost'].includes(status),
  };
}

export function sortStageKeyForUi(stage) {
  const mapping = {
    discovery: 'discovery',
    creative_ready: 'pitch',
    offer_in_progress: 'offer',
    awaiting_buyer: 'proposal',
    accepted_pending_billing: 'close',
    ready_to_launch: 'close',
    live: 'close',
    completed_or_terminal: 'close',
  };
  return mapping[stage] || 'discovery';
}

export function workspacePriorityScore(item) {
  let score = 0;
  if (item.needsAttention) score += 50;
  if (item.stale) score += 20;
  if (item.queueStage === 'ready_to_launch') score += 15;
  if (item.queueStage === 'accepted_pending_billing') score += 12;
  if (item.queueStage === 'awaiting_buyer') score += 10;
  score += STAGE_ORDER[sortStageKeyForUi(item.queueStage)] || 0;
  return score;
}

export function pipelineSummaryFromQueue(queueItems = []) {
  return {
    totalWorkspaces: queueItems.length,
    needsAttention: queueItems.filter((item) => item.needsAttention).length,
    liveCampaigns: queueItems.filter((item) => item.queueStage === 'live').length,
    awaitingBuyer: queueItems.filter((item) => item.queueStage === 'awaiting_buyer').length,
  };
}

export function buildReportNarrative(reportSummary, campaign, businessName) {
  if (reportSummary?.summary_text) return reportSummary.summary_text;
  const metrics = reportMetricsFromSnapshot(campaign?.latestSnapshot, reportSummary, campaign);
  return `${businessName || 'This campaign'} has delivered ${metrics.impressions.toLocaleString()} ad views with ${metrics.days_remaining} days remaining.`;
}

export function pricingExplainerFromSelectedPackage(selectedPackage = {}) {
  const pricing = selectedPackage.pricing || {};
  return {
    clientPriceLabel: formatDollars(pricing.sellPrice || 0),
    platformCostLabel: formatDollars(pricing.platformCost || 0),
    sellerEarningsLabel: formatDollars(pricing.sellerEarnings || 0),
    markupTierLabel: PRICING_TIERS.find((tier) => tier.id === pricing.tierId)?.label || 'Custom',
  };
}
